import type { Beat, Block, Island, Scenario } from '../types'
import { parsePinyin } from './pinyin'
import { gradeFor, languageOf, type LanguageCode } from './languages'
import { uid } from './id'
import { freshSrs } from './srs'

/**
 * The contract between a generated draft and content this app will accept.
 *
 * A model producing language-learning material fails in a particular way: the
 * output reads perfectly and is wrong underneath. Mandarin pinyin that does not
 * match its own characters is the worst case, because nothing surfaces it — the
 * learner types the correct answer, the grader disagrees, and they conclude
 * they are worse than they are. So a draft is checked against the same rules
 * the hand-written starter content is tested against, and content that fails
 * never reaches a user's island list.
 *
 * Shared deliberately: the server validates so it can retry a bad generation
 * before spending the user's patience, and the client validates again before
 * saving, because the server is not the source of truth here — the device is.
 */

/** Han characters only: punctuation carries no syllable. */
const hanCount = (s: string) => (s.match(/[一-鿿]/gu) ?? []).length

/** What the model is asked for: content, and nothing about bookkeeping. */
export interface BlockDraft {
  hanzi: string
  pinyin: string
  english: string
  note?: string
}

export interface IslandDraft {
  title: string
  titleZh: string
  useWhen: string
  tags: string[]
  hskLevel: number
  blocks: BlockDraft[]
}

export interface BeatDraft {
  speaker: string
  promptHanzi: string
  promptPinyin: string
  promptEnglish: string
  cue: string
  /**
   * 1-based index into the island list the model was shown — not an id.
   *
   * Ids here are opaque 13-character strings; asking a model to copy one back
   * exactly, dozens of times, invites a silent single-character slip that
   * points a beat at nothing. An index out of range is impossible to miss.
   */
  islandIndex: number
  /** 1-based block numbers within that island. Empty means any block counts. */
  blockNumbers: number[]
  hint?: string
}

export interface ScenarioDraft {
  title: string
  titleZh: string
  setting: string
  brief: string
  beats: BeatDraft[]
}

export interface Rejection {
  /** Where the problem is, in terms the prompt can act on. */
  where: string
  problem: string
}

export type Check<T> = { ok: true; value: T } | { ok: false; problems: Rejection[] }

export const MIN_BLOCKS = 3
export const MAX_BLOCKS = 10
export const MIN_BEATS = 3
export const MAX_BEATS = 8

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')
const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * Every rule a block must satisfy, checked in the language it was written for.
 *
 * The grade-against-itself check is the one that matters most: it is the only
 * check that exercises the same code path the learner will hit, so it catches
 * disagreements between the text and the grader that no amount of inspecting
 * the text would reveal.
 */
export function checkBlock(block: BlockDraft, language: LanguageCode, where: string): Rejection[] {
  const problems: Rejection[] = []
  const lang = languageOf(language)
  const target = text(block.hanzi)
  const phonetic = text(block.pinyin)

  if (!target) problems.push({ where, problem: `${lang.targetLabel} is empty` })
  if (!text(block.english)) problems.push({ where, problem: 'English is empty' })

  if (lang.hasPhonetics) {
    if (!phonetic) {
      problems.push({ where, problem: `${lang.phoneticLabel ?? 'phonetics'} is empty` })
    } else {
      const parsed = parsePinyin(phonetic)
      const unreadable = parsed.filter((s) => s.base === '').map((s) => s.raw)
      if (unreadable.length) {
        problems.push({
          where,
          problem: `these are not readable pinyin syllables: ${unreadable.join(', ')}`,
        })
      }
      const syllables = parsed.length
      const chars = hanCount(target)
      if (syllables !== chars) {
        problems.push({
          where,
          problem: `${chars} Han characters but ${syllables} pinyin syllables — one character is exactly one syllable, and punctuation is not a syllable`,
        })
      }
    }
  } else if (phonetic) {
    problems.push({ where, problem: `${lang.name} has no phonetic line; leave it empty` })
  }

  // Only worth running once the text is present; grading an empty string just
  // restates the emptiness.
  if (target && problems.length === 0) {
    const asBlock: Block = {
      id: 'check',
      hanzi: target,
      pinyin: phonetic,
      english: text(block.english),
    }
    const mode = language === 'zh' ? 'hanzi' : 'text'
    if (gradeFor(language, asBlock, target, mode, true).score !== 100) {
      problems.push({ where, problem: 'does not grade as correct when typed back exactly' })
    }
    if (language === 'zh' && gradeFor('zh', asBlock, phonetic, 'pinyin', true).score !== 100) {
      problems.push({ where, problem: 'the pinyin does not grade as correct against itself' })
    }
  }

  return problems
}

/** Validate a raw parsed JSON value as an island draft. */
export function checkIslandDraft(raw: unknown, language: LanguageCode): Check<IslandDraft> {
  const problems: Rejection[] = []
  if (!isObj(raw)) return { ok: false, problems: [{ where: 'island', problem: 'not an object' }] }

  const title = text(raw.title)
  const titleZh = text(raw.titleZh)
  const useWhen = text(raw.useWhen)
  if (!title) problems.push({ where: 'island', problem: 'title is empty' })
  if (!titleZh) problems.push({ where: 'island', problem: 'the native-language title is empty' })
  if (!useWhen) problems.push({ where: 'island', problem: 'useWhen is empty' })

  const rawBlocks = Array.isArray(raw.blocks) ? raw.blocks : []
  if (rawBlocks.length < MIN_BLOCKS || rawBlocks.length > MAX_BLOCKS) {
    problems.push({
      where: 'island',
      problem: `needs between ${MIN_BLOCKS} and ${MAX_BLOCKS} blocks, got ${rawBlocks.length}`,
    })
  }

  const blocks: BlockDraft[] = []
  rawBlocks.forEach((entry: unknown, i: number) => {
    const where = `block ${i + 1}`
    if (!isObj(entry)) {
      problems.push({ where, problem: 'not an object' })
      return
    }
    const block: BlockDraft = {
      hanzi: text(entry.hanzi),
      pinyin: text(entry.pinyin),
      english: text(entry.english),
      ...(text(entry.note) ? { note: text(entry.note) } : {}),
    }
    problems.push(...checkBlock(block, language, where))
    blocks.push(block)
  })

  // Duplicates are not a validation failure the model can always avoid, but a
  // block repeated twice is a block drilled twice for no benefit.
  const seen = new Set<string>()
  blocks.forEach((b, i) => {
    const key = b.hanzi.toLowerCase()
    if (key && seen.has(key)) {
      problems.push({ where: `block ${i + 1}`, problem: 'duplicates an earlier block' })
    }
    seen.add(key)
  })

  if (problems.length) return { ok: false, problems }

  const tags = Array.isArray(raw.tags)
    ? raw.tags.map((t: unknown) => text(t)).filter(Boolean).slice(0, 6)
    : []
  const level = Number(raw.hskLevel)

  return {
    ok: true,
    value: {
      title,
      titleZh,
      useWhen,
      tags,
      hskLevel: Number.isFinite(level) ? Math.min(6, Math.max(1, Math.round(level))) : 2,
      blocks,
    },
  }
}

/**
 * Validate a scenario draft against the islands it is allowed to point at.
 *
 * A beat whose island does not exist is a dead end mid-run, so the reference is
 * resolved here rather than trusted — and it is resolved against the caller's
 * own islands, which is also what stops a scenario from reaching into content
 * that is not theirs.
 */
export function checkScenarioDraft(
  raw: unknown,
  language: LanguageCode,
  islands: Island[],
): Check<ScenarioDraft> {
  const problems: Rejection[] = []
  if (!isObj(raw)) return { ok: false, problems: [{ where: 'scenario', problem: 'not an object' }] }

  const title = text(raw.title)
  const titleZh = text(raw.titleZh)
  const setting = text(raw.setting)
  const brief = text(raw.brief)
  if (!title) problems.push({ where: 'scenario', problem: 'title is empty' })
  if (!setting) problems.push({ where: 'scenario', problem: 'setting is empty' })
  if (!brief) problems.push({ where: 'scenario', problem: 'brief is empty' })

  const rawBeats = Array.isArray(raw.beats) ? raw.beats : []
  if (rawBeats.length < MIN_BEATS || rawBeats.length > MAX_BEATS) {
    problems.push({
      where: 'scenario',
      problem: `needs between ${MIN_BEATS} and ${MAX_BEATS} beats, got ${rawBeats.length}`,
    })
  }

  const beats: BeatDraft[] = []
  rawBeats.forEach((entry: unknown, i: number) => {
    const where = `beat ${i + 1}`
    if (!isObj(entry)) {
      problems.push({ where, problem: 'not an object' })
      return
    }

    const promptHanzi = text(entry.promptHanzi)
    const promptPinyin = text(entry.promptPinyin)
    if (!promptHanzi) problems.push({ where, problem: 'the prompt is empty' })
    if (!text(entry.cue)) problems.push({ where, problem: 'the cue is empty' })
    if (!text(entry.speaker)) problems.push({ where, problem: 'the speaker is empty' })

    // The prompt is spoken aloud, so it is held to the same transcription rule
    // as a block even though nobody types it.
    if (languageOf(language).hasPhonetics && promptHanzi) {
      if (!promptPinyin) {
        problems.push({ where, problem: 'the prompt has no pinyin' })
      } else {
        const parsed = parsePinyin(promptPinyin)
        if (parsed.filter((s) => s.base === '').length) {
          problems.push({ where, problem: `unreadable pinyin in the prompt: ${promptPinyin}` })
        } else if (parsed.length !== hanCount(promptHanzi)) {
          problems.push({
            where,
            problem: `the prompt has ${hanCount(promptHanzi)} characters but ${parsed.length} pinyin syllables`,
          })
        }
      }
    }

    const islandIndex = Number(entry.islandIndex)
    const island = islands[islandIndex - 1]
    if (!Number.isInteger(islandIndex) || !island) {
      problems.push({
        where,
        problem: `islandIndex must be between 1 and ${islands.length}, got ${String(entry.islandIndex)}`,
      })
    } else if (island.language !== language) {
      // ScenarioRun filters beats to the active language and drops the rest
      // without a word, so a cross-language beat would simply vanish mid-run.
      problems.push({
        where,
        problem: `island ${islandIndex} is ${island.language}, not ${language}`,
      })
    }

    const rawNumbers = Array.isArray(entry.blockNumbers) ? entry.blockNumbers : []
    const blockNumbers: number[] = []
    for (const n of rawNumbers) {
      const num = Number(n)
      if (!island) break
      if (!Number.isInteger(num) || num < 1 || num > island.blocks.length) {
        problems.push({
          where,
          problem: `blockNumbers must be between 1 and ${island.blocks.length} for that island, got ${String(n)}`,
        })
        continue
      }
      blockNumbers.push(num)
    }

    beats.push({
      speaker: text(entry.speaker),
      promptHanzi,
      promptPinyin,
      promptEnglish: text(entry.promptEnglish),
      cue: text(entry.cue),
      islandIndex,
      blockNumbers,
      ...(text(entry.hint) ? { hint: text(entry.hint) } : {}),
    })
  })

  if (problems.length) return { ok: false, problems }
  return { ok: true, value: { title, titleZh, setting, brief, beats } }
}

/**
 * Turn a validated draft into a real island.
 *
 * Everything the app owns rather than the model — ids, clocks, SRS state — is
 * stamped here. `isStarter` is false: this is the user's own content, and the
 * starter-content nudges and the backfill that re-seeds missing starters must
 * both leave it alone.
 */
export function materialiseIsland(draft: IslandDraft, language: LanguageCode): Island {
  const now = Date.now()
  return {
    id: uid('isl'),
    language,
    title: draft.title,
    titleZh: draft.titleZh,
    useWhen: draft.useWhen,
    tags: draft.tags,
    hskLevel: draft.hskLevel,
    blocks: draft.blocks.map<Block>((b) => ({
      id: uid('blk'),
      hanzi: b.hanzi,
      pinyin: b.pinyin,
      english: b.english,
      ...(b.note ? { note: b.note } : {}),
    })),
    srs: freshSrs(),
    stats: { performances: 0, bestScore: 0, lastScore: null, lastDrilledAt: null },
    isStarter: false,
    createdAt: now,
    updatedAt: now,
  }
}

/** As above, resolving each beat's island index back to a real id. */
export function materialiseScenario(
  draft: ScenarioDraft,
  language: LanguageCode,
  islands: Island[],
): Scenario {
  return {
    id: uid('scn'),
    language,
    title: draft.title,
    titleZh: draft.titleZh,
    setting: draft.setting,
    brief: draft.brief,
    isStarter: false,
    updatedAt: Date.now(),
    stats: { runs: 0, bestScore: 0, lastScore: null, lastRunAt: null },
    beats: draft.beats.map<Beat>((b) => {
      const island = islands[b.islandIndex - 1]
      return {
        id: uid('bea'),
        speaker: b.speaker,
        promptHanzi: b.promptHanzi,
        promptPinyin: b.promptPinyin,
        promptEnglish: b.promptEnglish,
        cue: b.cue,
        expectIslandId: island.id,
        expectBlockIds: b.blockNumbers.map((n) => island.blocks[n - 1].id),
        ...(b.hint ? { hint: b.hint } : {}),
      }
    }),
  }
}
